/**
 * Hook to get the current primary brand color as a concrete color value.
 *
 * --color-tide is derived in CSS (from --brand-primary, lifted via color-mix
 * in dark mode), so the raw custom-property text isn't usable in SVG
 * attributes. A probe element resolves it to an rgb() string. Re-resolves
 * when branding (inline style) or theme (class) changes on <html>.
 */
import * as React from "react";

const DEFAULT_COLOR = "#2663c7";

function resolvePrimaryColor(): string {
  const probe = document.createElement("span");
  probe.style.display = "none";
  probe.style.color = "var(--color-tide)";
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || DEFAULT_COLOR;
}

export function usePrimaryColor(): string {
  const [color, setColor] = React.useState<string>(DEFAULT_COLOR);

  React.useEffect(() => {
    const update = () => setColor(resolvePrimaryColor());
    update();

    // Branding sets inline style on <html>; the theme toggle flips its class.
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    return () => observer.disconnect();
  }, []);

  return color;
}
